import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';
import { TravelerRefSchema } from '../../shared/schema/traveler_ref.js';
import { CheckoutItemSchema } from '../product_checkout/product_checkout_schema.js';

/**
 * Próximos serviços oferecidos após a reserva (design #2116, story "Serviços
 * Complementares — Pós-emissão do aéreo"). Conjunto fechado: cada id mapeia
 * para rótulo/ícone/ask no renderer via i18n do kit.
 *
 * Os `transfer_*` são os traslados da viagem (agendamento de carro): até o
 * aeroporto, do aeroporto ao hotel e até o hotel. O toque neles manda o pedido
 * com `domainHint: 'mobility'`, e o supervisor entra direto no domínio, sem
 * classificar a prosa.
 *
 * `flight` ("Buscar voos") é a linha do pós-ônibus (frame 09 do "Conversacional
 * → Bus"); o pós-aéreo e o pós-hotel não a sugerem por padrão.
 */
export const NextServiceIdSchema = z.enum([
  'hotel',
  'flight',
  'seat',
  'car',
  'train',
  'bus',
  'insurance',
  'transfer_to_airport',
  'transfer_airport_hotel',
  'transfer_to_hotel',
]);
export type NextServiceId = z.infer<typeof NextServiceIdSchema>;

export const NextServiceSuggestionSchema = z
  .object({
    service: NextServiceIdSchema,
    recommended: z.boolean().optional(),
    // Fato consultivo após o `·` no rótulo ("Reservar assento · Grátis com seu
    // TudoAzul"). O componente NUNCA o inventa: o agents injeta só o fato que o
    // contexto tem (tarifa, fidelidade, política); ausente → só a ação.
    note: z.string().min(1).optional(),
  })
  .strict();
export type NextServiceSuggestion = z.infer<typeof NextServiceSuggestionSchema>;

type ProductType = z.infer<typeof CheckoutItemSchema>['productType'];

/**
 * O que todo `*_next_services` carrega (#2116): o turno da reserva emite o
 * bloco com as sugestões do próximo serviço e o carrinho do turno, no MESMO
 * shape do `product_checkout`, e o host publica o recibo provisório do painel
 * da viagem direto daqui (anti-N+1). Um kind por produto no wire: o produto só
 * muda o item que o carrinho exige.
 *
 * - `challengeId`: o checkout que fez esta reserva (#5360), a chave do desfecho
 *   do card 1click, lida também no replay. Ausente nos blocos antigos.
 * - `tripId`: opcional porque o write-ahead pode não ter resolvido a viagem no
 *   turno; sem ele o card segue útil, só o painel da viagem não abre.
 * - `outcome`: espelha o recibo provisório do cliente — `reserved` («Em
 *   aprovação» no painel) ou `in_progress` (o fornecedor ainda processando).
 */
function blockShape<K extends string>(kind: K, productType: ProductType, itemLabel: string) {
  return {
    ...chatUiBase,
    kind: z.literal(kind),
    challengeId: z.string().min(1).optional(),
    tripId: z.string().min(1).optional(),
    outcome: z.enum(['reserved', 'in_progress']),
    // Exige ao menos UM item do produto: as asks das linhas derivam dele.
    items: z
      .array(CheckoutItemSchema)
      .min(1)
      .refine(
        (items) => items.some((item) => item.productType === productType),
        `${kind}: requer ao menos um item de ${itemLabel}`,
      ),
    travelers: z.array(TravelerRefSchema).min(1).optional(),
  };
}

function servicesOf(kind: string) {
  return z
    .array(NextServiceSuggestionSchema)
    .min(1)
    .refine(
      (list) => new Set(list.map((s) => s.service)).size === list.length,
      `${kind}: serviço repetido na lista`,
    );
}

/** O pós-aéreo (#2116): substituiu o recibo `flight_reservation`. */
export const FlightNextServicesBlockSchema = z
  .object({
    ...blockShape('flight_next_services', 'flight', 'voo'),
    services: servicesOf('flight_next_services'),
  })
  .strict();
export type FlightNextServicesBlock = z.infer<typeof FlightNextServicesBlockSchema>;

/** O pós-hotel (#2349). `services` opcional:
 *  os blocos da era DATA-ONLY do histórico chegam sem a lista e não desenham. */
export const HotelNextServicesBlockSchema = z
  .object({
    ...blockShape('hotel_next_services', 'hotel', 'hotel'),
    services: servicesOf('hotel_next_services').optional(),
  })
  .strict();
export type HotelNextServicesBlock = z.infer<typeof HotelNextServicesBlockSchema>;

/** O pós-passagem de ônibus (frame 09 do Figma "Conversacional → Bus"): o
 *  ÚNICO bloco do turno, sem recibo. */
export const BusNextServicesBlockSchema = z
  .object({
    ...blockShape('bus_next_services', 'bus', 'ônibus'),
    services: servicesOf('bus_next_services'),
  })
  .strict();
export type BusNextServicesBlock = z.infer<typeof BusNextServicesBlockSchema>;

/** O pós-carro (#5514): o ÚNICO bloco do turno e o sinal «entrou na viagem». */
export const RentalNextServicesBlockSchema = z
  .object({
    ...blockShape('rental_next_services', 'rental', 'carro'),
    services: servicesOf('rental_next_services'),
  })
  .strict();
export type RentalNextServicesBlock = z.infer<typeof RentalNextServicesBlockSchema>;

/** O pós-corrida (#5279/#5330, sugestões desde a #5514). `services` opcional:
 *  os blocos de antes são DATA-ONLY (o sinal «entrou na viagem») e não desenham. */
export const MobilityNextServicesBlockSchema = z
  .object({
    ...blockShape('mobility_next_services', 'mobility', 'mobilidade'),
    services: servicesOf('mobility_next_services').optional(),
  })
  .strict();
export type MobilityNextServicesBlock = z.infer<typeof MobilityNextServicesBlockSchema>;

/** Qualquer `*_next_services`: o host adota todos do mesmo jeito (recibo
 *  provisório, painel da viagem, desfecho do 1click) e só precisa saber de
 *  qual produto é (`NEXT_SERVICES_PRODUCT`). */
export const NextServicesBlockSchema = z.discriminatedUnion('kind', [
  FlightNextServicesBlockSchema,
  HotelNextServicesBlockSchema,
  BusNextServicesBlockSchema,
  RentalNextServicesBlockSchema,
  MobilityNextServicesBlockSchema,
]);
export type NextServicesBlock = z.infer<typeof NextServicesBlockSchema>;

/** O bloco de cada produto — a chave dos contratos do organismo. */
export interface NextServicesBlockMap {
  flight: FlightNextServicesBlock;
  hotel: HotelNextServicesBlock;
  bus: BusNextServicesBlock;
  rental: RentalNextServicesBlock;
  mobility: MobilityNextServicesBlock;
}
export type NextServicesProduct = keyof NextServicesBlockMap;

/** O produto de cada kind do wire. */
export const NEXT_SERVICES_PRODUCT: {
  readonly [K in NextServicesBlock['kind']]: NextServicesProduct;
} = {
  flight_next_services: 'flight',
  hotel_next_services: 'hotel',
  bus_next_services: 'bus',
  rental_next_services: 'rental',
  mobility_next_services: 'mobility',
};
